import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { turnstileLoader } from '../../helpers/scriptLoaders';

const CDATA_RECREATE_DEBOUNCE_MS = 600;

class CloudflareCaptcha extends Component {
  componentDidMount() {
    this.loadWidget();
  }

  componentDidUpdate(prevProps) {
    if (this.props.doReset && this.props.doReset !== prevProps.doReset) {
      this.reset();
    } else if (this.widgetId === undefined && !this.loadingWidget) {
      this.loadWidget();
    } else if (this.props.cdata !== prevProps.cdata && this.widgetId !== undefined) {
      this.scheduleRecreate();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.recreateTimeout);
    if (window.turnstile && this.widgetId !== undefined) {
      window.turnstile.remove(this.widgetId);
    }
  }

  reset = () => {
    if (window.turnstile && this.widgetId !== undefined) {
      window.turnstile.reset(this.widgetId);
    }
  };

  execute = () => {
    if (window.turnstile && this.widgetId !== undefined) {
      window.turnstile.execute(this.widgetId);
    }
  };

  scheduleRecreate = () => {
    clearTimeout(this.recreateTimeout);
    this.recreateTimeout = setTimeout(this.recreateWidget, CDATA_RECREATE_DEBOUNCE_MS);
  };

  recreateWidget = () => {
    if (this.unmounted) {
      return;
    }

    if (window.turnstile && this.widgetId !== undefined) {
      window.turnstile.remove(this.widgetId);
    }

    this.widgetId = undefined;
    this.props.fetchToken(null);
    this.renderWidget();
  };

  handleChangeCaptchaWaiting = (isWaiting) => {
    if (this.unmounted) { return; }

    if (this.props.onWaitingChange) {
      this.props.onWaitingChange(isWaiting);
    }
  }

  handleChangeCaptchaRequired = (isRequired) => {
    if (this.unmounted) { return; }

    if (this.props.onRequiredCaptcha) {
      this.props.onRequiredCaptcha(isRequired);
    }
  }

  loadWidget = () => {
    const sitekey = process.env.CLOUDFLARE_CAPTCHA_SITE_KEY;

    if (!sitekey) {
      console.warn('Cloudflare captcha site key is missing');
      return;
    }

    this.loadingWidget = true;
    this.handleChangeCaptchaWaiting(true);
    turnstileLoader()
      .then(() => {
        this.loadingWidget = false;
        if (this.unmounted) {
          return;
        }
        this.handleChangeCaptchaWaiting(false);
        this.renderWidget(sitekey);
      })
      .catch((err) => {
        this.loadingWidget = false;
        if (this.unmounted) { return; }
        this.handleChangeCaptchaWaiting(false);
        console.error('Cloudflare captcha script failed to load', err);
      });
  }

  renderWidget = (sitekey = process.env.CLOUDFLARE_CAPTCHA_SITE_KEY) => {
    if (!sitekey) {
      console.warn('Cloudflare captcha site key is missing');
      return;
    }

    if (!window.turnstile || !this.container) {
      return;
    }

    if (this.widgetId !== undefined) {
      return;
    }

    this.widgetId = window.turnstile.render(this.container, {
      sitekey,
      action: this.props.action,
      cdata: this.props.cdata,
      theme: 'light',
      size: 'flexible',
      appearance: 'interaction-only',
      execution: this.props.execute ? 'execute' : undefined,

      callback: (token) => {
        this.props.fetchToken(token);
        this.handleChangeCaptchaWaiting(false);
        this.handleChangeCaptchaRequired(false);
      },

      'before-interactive-callback': () => {
        this.handleChangeCaptchaWaiting(false);
        this.handleChangeCaptchaRequired(true);
      },

      'expired-callback': () => {
        this.handleChangeCaptchaWaiting(false);
        this.props.fetchToken(null);
      },

      'error-callback': () => {
        this.handleChangeCaptchaWaiting(false);
        this.props.fetchToken(null);
      }
    });

    if (this.props.onWidgetLoad) {
      this.props.onWidgetLoad(this.widgetId);
    }
  };

  render() {
    return <div ref={(el) => { this.container = el; }} />;
  }
}

CloudflareCaptcha.propTypes = {
  fetchToken: PropTypes.func,
  doReset: PropTypes.bool,
  action: PropTypes.string,
  cdata: PropTypes.string,
  onWidgetLoad: PropTypes.func,
  execute: PropTypes.bool,
  onWaitingChange: PropTypes.func,
  onRequiredCaptcha: PropTypes.func
};

export default CloudflareCaptcha;
